import React, { useState } from "react";
import { 
  FiCheckCircle, 
  FiClock, 
  FiUsers, 
  FiShield, 
  FiAlertTriangle, 
  FiLayers, 
  FiFileText, 
  FiArrowRight,
  FiAward
} from "react-icons/fi";
import type { 
  EightDReport, 
  EightDDiscipline, 
  EightDDisciplineCode,
  RcaMethodCode 
} from "../../../../types/rca.types.js";

export type EightDWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  readOnly?: boolean;
  onSave?: (report: EightDReport) => void;
  onChainMethod?: (method: RcaMethodCode) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const DISCIPLINES: { code: EightDDisciplineCode; name: string; title: string; desc: string }[] = [
  { code: "D0", name: "Emergency Response", title: "D0: Emergency Response", desc: "Protect customers immediately and initiate containment sorting." },
  { code: "D1", name: "Team Formation", title: "D1: Cross-Functional Team", desc: "Appoint team leader, champion, and cross-functional members." },
  { code: "D2", name: "Problem Description", title: "D2: Problem Description", desc: "Define problem using 5W2H / Is-Is-Not boundary data." },
  { code: "D3", name: "Interim Containment", title: "D3: Interim Containment Actions", desc: "Isolate defectives, quarantine affected lots, verify 100% sort." },
  { code: "D4", name: "Root Cause & Escape", title: "D4: Root Cause & Escape Point", desc: "Identify occurrence root cause and escape root cause via 5-Why/Fishbone." },
  { code: "D5", name: "Permanent Corrective Actions", title: "D5: Select PCA", desc: "Evaluate and choose permanent corrective actions addressing root cause." },
  { code: "D6", name: "Implement & Validate", title: "D6: Implement & Validate PCA", desc: "Verify long-term effectiveness of corrective actions." },
  { code: "D7", name: "Prevent Recurrence", title: "D7: Preventive Actions", desc: "Update PFMEA, control plans, work instructions across similar parts." },
  { code: "D8", name: "Recognize & Close", title: "D8: Team Recognition & Closure", desc: "Document lessons learned, celebrate team contributions, finalize report." },
];

export function EightDWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onSave,
  onChainMethod,
  onRootCauseIdentified,
}: EightDWorkspaceProps) {
  const [selectedDiscipline, setSelectedDiscipline] = useState<EightDDisciplineCode>("D4");
  const [disciplineStates, setDisciplineStates] = useState<Record<EightDDisciplineCode, { status: "COMPLETED" | "IN_PROGRESS" | "NOT_STARTED"; text: string }>>({
    D0: { status: "COMPLETED", text: "Emergency sort initiated on finished stock LOT-2026-0789. 125 pcs quarantined." },
    D1: { status: "COMPLETED", text: "Team Lead: John Smith (Quality). Members: Kumar (Prod), Arun (Eng), Priya (QA)." },
    D2: { status: "COMPLETED", text: problemStatement || "Product dimension out of tolerance on part PN-12345 by +0.08mm." },
    D3: { status: "COMPLETED", text: "100% CMM gauging implemented at station 3. Zero escapes to customer." },
    D4: { status: "IN_PROGRESS", text: "Occurrence Cause: CNC spindle Z-axis thermal drift. Escape Cause: In-process gauge uncalibrated." },
    D5: { status: "NOT_STARTED", text: "PCA Candidate: Install closed-loop thermal compensation probe and automatic tool offset." },
    D6: { status: "NOT_STARTED", text: "Effectiveness run: 5 consecutive production batches with Cpk > 1.67." },
    D7: { status: "NOT_STARTED", text: "Update PFMEA-2026-001 Rev 4 and CNC Control Plan CP-CNC-02." },
    D8: { status: "NOT_STARTED", text: "Lessons learned uploaded to CMTS Knowledge Base." },
  });

  const handleUpdateCurrentDiscipline = (text: string, status: "COMPLETED" | "IN_PROGRESS" | "NOT_STARTED") => {
    if (readOnly) return;
    setDisciplineStates(prev => ({
      ...prev,
      [selectedDiscipline]: { status, text }
    }));

    if (selectedDiscipline === "D4" && status === "COMPLETED") {
      onRootCauseIdentified?.(text, "MACHINE", 92);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            8D
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              8D Problem Solving Methodology
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-50 text-orange-800 border border-orange-200">
                Automotive & Industrial Standard
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Structured 8-discipline problem solving process ensuring containment, verified root cause, permanent correction, and systemic prevention.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onChainMethod?.("FIVE_WHY")}
          className="px-3.5 py-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
        >
          Chain 5-Why into D4 →
        </button>
      </div>

      {/* D0-D8 Stepper Strip */}
      <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-[750px]">
          {DISCIPLINES.map(d => {
            const current = disciplineStates[d.code];
            const isSelected = selectedDiscipline === d.code;
            return (
              <button
                key={d.code}
                type="button"
                onClick={() => setSelectedDiscipline(d.code)}
                className={`flex-1 p-2 rounded-lg border text-left transition-all ${
                  isSelected 
                    ? "bg-white border-orange-500 ring-2 ring-orange-100 shadow-sm" 
                    : "bg-white border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between text-[11px] mb-0.5">
                  <span className="font-bold text-orange-700">{d.code}</span>
                  {current?.status === "COMPLETED" ? (
                    <FiCheckCircle className="w-3 h-3 text-emerald-600" />
                  ) : current?.status === "IN_PROGRESS" ? (
                    <FiClock className="w-3 h-3 text-amber-500" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-slate-300" />
                  )}
                </div>
                <div className="text-[10px] font-semibold text-slate-800 truncate">
                  {d.name}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Discipline Editor */}
      <div className="p-6 space-y-4">
        {(() => {
          const activeMeta = DISCIPLINES.find(d => d.code === selectedDiscipline)!;
          const current = disciplineStates[selectedDiscipline];

          return (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    {activeMeta.title}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">{activeMeta.desc}</p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Status:</span>
                  <select
                    value={current?.status || "NOT_STARTED"}
                    disabled={readOnly}
                    onChange={(e) => handleUpdateCurrentDiscipline(current?.text || "", e.target.value as any)}
                    className="text-xs p-1.5 rounded-lg border border-slate-300 bg-white font-semibold text-slate-800"
                  >
                    <option value="NOT_STARTED">Not Started</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
              </div>

              {/* Form Area */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Discipline Findings & Evidence Summary:
                </label>
                <textarea
                  rows={4}
                  value={current?.text || ""}
                  disabled={readOnly}
                  onChange={(e) => handleUpdateCurrentDiscipline(e.target.value, current?.status || "IN_PROGRESS")}
                  placeholder={`Document findings for ${activeMeta.code}...`}
                  className="w-full text-xs p-3 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* D4 Special Chaining Box */}
              {selectedDiscipline === "D4" && (
                <div className="p-4 bg-orange-50/60 rounded-xl border border-orange-200 flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-orange-900">RCA Method Chaining for D4:</h4>
                    <p className="text-xs text-orange-800">
                      Use 5-Why or Fishbone directly to discover and prove the occurrence cause and escape point.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onChainMethod?.("FIVE_WHY")}
                    className="px-3.5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
                  >
                    Open 5-Why in D4
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
